import { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  FiArrowRight,
  FiDownload,
  FiLoader,
  FiRefreshCw,
} from 'react-icons/fi';
import Text from 'src/design/styleguide/text/Text';
import PortfolioOverTimeChart from 'src/components/portfolioWidget/portfolioOverTimeChart/PortfolioOverTimeChart';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import {
  breakpointSmall,
  breakpointXSmall,
} from 'src/design/styleguide/common/breakpoints';
import { themeForAdvisorAccount } from 'src/themes/landingPageThemes';
import { greenDark, greenLight, pink700 } from 'src/themes/brickx/colors';
import {
  getAdvisorDateTimestamp,
  getDocumentFinancialYears,
  getSignedOrderAmount,
} from '../advisorClientDetailData';
import { useClientDetail } from './ClientDetailContext';
import {
  AccountTypeBadge,
  Card,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableScroll,
  DetailDescription,
  DetailLabel,
  DetailRow,
  DetailTerm,
  DetailValue,
  DocumentGlyph,
  DocumentIdentity,
  DocumentMeta,
  DocumentName,
  DocumentRow,
  DocumentRows,
  DownloadButton,
  EmptyCopy,
  StatusPill,
  TransactionAmount,
  TransactionDescription,
  TransactionTypeBadge,
} from './styles';
import { usePortfolioHistory } from './hooks';
import {
  TRANSACTION_TYPES,
  formatCount,
  formatCurrency,
  formatDate,
  formatFinancialYearRange,
  formatPercentage,
  formatStatus,
  getAccountTypeTone,
  getDocumentIcon,
  getTransactionsWithActivity,
  getValueTone,
} from './helpers';
import { DetailRows, OrdersPropertyCode } from './components';

const PROPERTY_BADGE_COLORS = [
  ['var(--bx-color---purple-50)', 'var(--bx-color---purple-600)'],
  ['var(--bx-color---pink-50)', 'var(--bx-color---pink-600)'],
  [greenLight, greenDark],
  ['var(--bx-color---blue-50)', 'var(--bx-color---blue-600)'],
  ['var(--bx-color---yellow-50)', 'var(--bx-color---yellow-800)'],
];

const OverviewColumn = styled.div`
  display: grid;
  min-width: 0;
  gap: ${gridUnit * 4}px;
`;

const InlineAction = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: ${gridUnit * 2}px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${themeForAdvisorAccount.colors.primary};
  font-family: inherit;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &:focus,
  &:active {
    color: ${themeForAdvisorAccount.colors.primary};
    outline: 0;
  }

  &:focus-visible {
    outline: 2px solid var(--bx-color---purple-300);
    outline-offset: 4px;
  }
`;

const PortfolioSnapshot = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${gridUnit * 4}px;
  padding: 0 var(--advisor-card-gutter) ${gridUnit * 6}px;

  ${breakpointXSmall(`
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  `)}
`;

const PortfolioPrimaryValue = styled.div`
  min-width: 0;
`;

const OverviewHistoryBody = styled.div`
  display: flex;
  min-width: 0;
  height: 168px;
  flex-direction: column;
  justify-content: center;
  margin-top: ${gridUnit * 3}px;

  > div {
    width: 100%;
    min-width: 0;
  }
`;

const SnapshotMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${gridUnit * 4}px;
  align-content: start;
  border-top: 1px solid var(--bx-color---grey-200);
  padding-top: ${gridUnit * 4}px;

  ${breakpointXSmall(`
    border-top: 0;
    padding-top: 0;
    border-left: 1px solid var(--bx-color---grey-200);
    padding-left: ${gridUnit * 4}px;
  `)}
`;

const SnapshotMetric = styled.div`
  min-width: 0;
  padding-bottom: ${gridUnit * 4}px;
  border-bottom: 1px solid var(--bx-color---grey-200);

  ${breakpointXSmall(`
    &:nth-child(-n + 2) {
      grid-column: 1 / -1;
    }
  `)}

  &:nth-child(n + 3) {
    padding-bottom: 0;
    border-bottom: 0;
  }

  ${DetailValue} {
    font-size: 1.6rem;
    overflow-wrap: anywhere;
  }
`;

const HoldingsSection = styled.div`
  border-top: 1px solid var(--bx-color---grey-200);
`;

const HoldingRows = styled.div`
  padding: 0 var(--advisor-card-gutter);
`;

const HoldingRow = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${gridUnit * 3}px;
  align-items: center;
  padding: ${gridUnit * 4}px 0;
  border-bottom: 1px solid var(--bx-color---grey-200);

  &:last-child {
    border-bottom: 0;
  }

  ${breakpointXSmall(`
    grid-template-columns: minmax(0, 1fr) minmax(100px, auto) minmax(76px, auto);
  `)}
`;

const HoldingIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${gridUnit * 4}px;

  > div {
    min-width: 0;
  }
`;

const PropertyBadge = styled.span`
  display: inline-flex;
  min-width: 56px;
  height: 38px;
  flex: 0 0 56px;
  align-items: center;
  justify-content: center;
  padding: 0 ${gridUnit * 2}px;
  border-radius: 6px;
  background: ${(props) => props.$background};
  color: ${(props) => props.$color};
  font-size: 1.3rem;
  font-weight: 700;
`;

const HoldingName = styled(Text).attrs({
  as: 'p',
  fontSize: '4x-small',
  fontWeight: 'medium',
})`
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--bx-color---grey-800);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HoldingMeta = styled(Text).attrs({ as: 'p', fontSize: '5x-small' })`
  margin: ${gridUnit}px 0 0;
  color: var(--bx-color---grey-500);
`;

const HoldingValue = styled(Text).attrs({
  as: 'p',
  fontSize: '4x-small',
  fontWeight: 'medium',
})`
  margin: 0;
  color: var(--bx-color---grey-800);
  text-align: right;
  white-space: nowrap;
`;

const HoldingRoi = styled(HoldingValue)`
  display: none;

  &.positive {
    color: ${greenDark};
  }

  &.negative {
    color: ${pink700};
  }

  ${breakpointXSmall(`display: block;`)}
`;

const CardFooter = styled.footer`
  display: flex;
  min-height: 56px;
  align-items: center;
  padding: ${gridUnit * 6}px var(--advisor-card-gutter);
  border-top: 1px solid var(--bx-color---grey-200);
`;

const OverviewTransactionsLayout = styled.div`
  display: flex;
  min-width: 0;

  > ${Card} {
    flex: 1;
  }
`;

const OverviewActivityCard = styled(Card)`
  ${CardHeader} {
    min-height: 72px;
  }

  ${CardTitle} {
    line-height: 1.2;
  }

  ${DataTableScroll} {
    flex-shrink: 0;
    width: calc(100% - ${gridUnit * 8}px);
    max-width: calc(100% - ${gridUnit * 8}px);
    margin-right: ${gridUnit * 4}px;
    margin-left: ${gridUnit * 4}px;
    border-top: 0;

    ${breakpointSmall(`
      width: calc(100% - ${gridUnit * 12}px);
      max-width: calc(100% - ${gridUnit * 12}px);
      margin-right: ${gridUnit * 6}px;
      margin-left: ${gridUnit * 6}px;
    `)}

    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: var(--bx-color---grey-50);
    }

    &::-webkit-scrollbar-thumb {
      border: 2px solid var(--bx-color---grey-50);
      border-radius: 8px;
      background: var(--bx-color---grey-300);
    }
  }

  ${DataTable} {
    min-width: 0;
    table-layout: auto;
    color: var(--bx-color---grey-800);
    font-size: 16px;
    line-height: 1.4;

    th,
    td {
      width: auto;
      height: 51px;
      box-sizing: border-box;
      padding: 0 ${gridUnit * 3}px;
      border-bottom: 1px solid var(--bx-color---grey-100);
      color: var(--bx-color---grey-800);
      font-size: 16px;
      font-weight: 400;
      text-align: left;
      vertical-align: middle;
      white-space: nowrap;
    }

    tr {
      height: 51px;
    }

    .activity-description {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      max-width: none;
      max-height: 1.4em;
      overflow: hidden;
      overflow-wrap: anywhere;
      white-space: normal;
    }

    th:last-child,
    td:last-child {
      padding-right: 0;
    }

    th:first-child,
    td:first-child {
      padding-left: 0;
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }

    .numeric {
      text-align: left;
    }

    .activity-type {
      display: inline-flex;
      max-width: 100%;
      box-sizing: border-box;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      vertical-align: middle;
    }

    .activity-stacked-only {
      display: none;
    }

    @media (min-width: 1100px) {
      .activity-quantity {
        padding-right: 0;
      }
    }

    @media (max-width: 1099px) {
      min-width: 420px;
      table-layout: fixed;

      col.activity-type-column {
        width: 15%;
      }

      col.activity-investment-column {
        width: 25%;
      }

      col.activity-amount-column {
        width: 120px;
      }

      .activity-stacked-only {
        display: table-cell;
      }

      col.activity-stacked-only {
        display: table-column;
      }

      @media (max-width: 575px) {
        col.activity-amount-column {
          width: 112px;
        }
      }
    }
  }

  ${StatusPill} {
    height: 32px;
    min-height: 0;
    box-sizing: border-box;
    margin: 0;
    padding: ${gridUnit}px ${gridUnit * 2}px;
    border-radius: 4px;
    font-size: 16px;
    line-height: 1.4;
  }
`;

const PropertyCodeBadge = ({ holding, index }) => {
  const colors = PROPERTY_BADGE_COLORS[index % PROPERTY_BADGE_COLORS.length];
  return (
    <PropertyBadge $background={colors[0]} $color={colors[1]}>
      {holding.propertyCode || '—'}
    </PropertyBadge>
  );
};

PropertyCodeBadge.propTypes = {
  holding: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

const OverviewLayout = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr);
  gap: ${gridUnit * 4}px;

  @media (min-width: 1100px) {
    grid-template-columns: minmax(0, 3fr) minmax(280px, 2fr);
    align-items: stretch;

    .overview-summary-content,
    .overview-client-content {
      min-height: var(--overview-summary-height, 0px);
      flex-shrink: 0;
    }

    ${OverviewColumn}[data-overview-section='identity'] {
      grid-template-rows: auto minmax(min-content, 1fr);
    }
  }

  ${Card} {
    display: flex;
    flex-direction: column;
  }

  ${CardHeader} {
    flex-shrink: 0;
  }

  ${CardTitle} {
    font-size: 20px;
  }

  ${DetailLabel}, ${DetailValue}, ${HoldingName}, ${HoldingValue},
  ${DetailTerm}, ${DetailDescription}, ${DocumentName}, ${DocumentMeta} {
    font-size: 16px;
    line-height: 1.4;
  }

  ${SnapshotMetric} ${DetailValue} {
    font-size: 20px;
  }

  ${PortfolioPrimaryValue} > ${DetailValue} {
    font-size: 20px;
    line-height: 1.4;
  }

  ${DocumentRow} {
    padding-top: ${gridUnit * 6}px;
    padding-bottom: ${gridUnit * 6}px;
  }

  ${DocumentName}, ${DocumentMeta} {
    line-height: 1.25;
  }

  ${DocumentMeta} {
    margin-top: ${gridUnit / 2}px;
  }

  .overview-documents ${DocumentRows} {
    display: grid;
    flex: 1;
    padding-bottom: 0;
    grid-template-rows: repeat(2, minmax(52px, 1fr));
  }

  .overview-documents ${DocumentGlyph} {
    width: 34px;
    height: 34px;
    flex-basis: 34px;
    border-radius: 5px;
  }

  .overview-documents ${DownloadButton} {
    width: 30px;
    height: 30px;
    flex-basis: 30px;
    border-radius: 5px;
  }

  ${InlineAction}, ${HoldingMeta}, ${PropertyBadge}, ${StatusPill}, ${EmptyCopy} {
    font-size: 16px;
    line-height: 1.4;
  }

  .overview-client-details ${DetailRow} {
    grid-template-columns: 64px minmax(0, 1fr);
    gap: ${gridUnit * 3}px;
  }

  .overview-client-details ${DetailTerm} {
    font-weight: 400;
  }

  .overview-client-details ${DetailDescription} {
    font-weight: 500;
  }

  .overview-client-details .client-address {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    max-height: 2.8em;
    overflow: hidden;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  ${HoldingRow} {
    padding-top: ${gridUnit * 2}px;
    padding-bottom: ${gridUnit * 2}px;
  }

  ${HoldingsSection} {
    display: flex;
    flex: 1;
    flex-direction: column;
  }

  ${HoldingRows} {
    display: grid;
    flex: 1;
    grid-auto-rows: minmax(56px, 1fr);
  }

  ${CardFooter} {
    flex-shrink: 0;
    min-height: 72px;
    box-sizing: border-box;
    margin-top: auto;
  }

  ${HoldingsSection}, ${CardFooter} {
    position: relative;
    border-top-color: transparent;

    &::before {
      position: absolute;
      top: -1px;
      right: var(--advisor-card-gutter);
      left: var(--advisor-card-gutter);
      height: 1px;
      background: var(--bx-color---grey-200);
      content: '';
    }
  }
`;

const OverviewAccountHistory = ({ clientId }) => {
  const { loading, data, error, retry } = usePortfolioHistory(clientId);
  const hasHistory = data.some(
    (plan) => plan.accountValueAcrossTime.length > 0,
  );
  return (
    <OverviewHistoryBody
      aria-busy={loading}
      data-test-reference="overview-account-history"
    >
      {loading ? (
        <DetailLabel>Loading account history...</DetailLabel>
      ) : error ? (
        <Fragment>
          <DetailLabel>Unable to load account history.</DetailLabel>
          <InlineAction type="button" onClick={retry}>
            <FiRefreshCw aria-hidden="true" />
            Retry
          </InlineAction>
        </Fragment>
      ) : hasHistory ? (
        <PortfolioOverTimeChart data={data} compact />
      ) : (
        <DetailLabel>No account history available.</DetailLabel>
      )}
    </OverviewHistoryBody>
  );
};

OverviewAccountHistory.propTypes = { clientId: PropTypes.string.isRequired };

const OverviewPortfolioSummary = ({
  clientId,
  detail,
  onViewPortfolio,
  summaryRef,
}) => {
  const { financials, holdings } = detail;
  const topHoldings = holdings.slice(0, 4);

  return (
    <Card data-overview-section="portfolio">
      <div className="overview-summary-content" ref={summaryRef}>
        <CardHeader>
          <CardTitle>Portfolio Summary</CardTitle>
        </CardHeader>
        <PortfolioSnapshot>
          <PortfolioPrimaryValue>
            <DetailLabel>Portfolio Value</DetailLabel>
            <DetailValue>
              {formatCurrency(financials.portfolioValue)}
            </DetailValue>
            <OverviewAccountHistory clientId={clientId} />
          </PortfolioPrimaryValue>
          <SnapshotMetrics>
            <SnapshotMetric>
              <DetailLabel>Amount Invested</DetailLabel>
              <DetailValue>
                {formatCurrency(financials.amountInvested)}
              </DetailValue>
            </SnapshotMetric>
            <SnapshotMetric>
              <DetailLabel>ROI</DetailLabel>
              <DetailValue className={getValueTone(financials.totalRoi)}>
                {formatPercentage(financials.totalRoi)}
              </DetailValue>
            </SnapshotMetric>
            <SnapshotMetric>
              <DetailLabel>Investments</DetailLabel>
              <DetailValue>{formatCount(financials.properties)}</DetailValue>
            </SnapshotMetric>
            <SnapshotMetric>
              <DetailLabel>Bricks</DetailLabel>
              <DetailValue>{formatCount(financials.bricks)}</DetailValue>
            </SnapshotMetric>
          </SnapshotMetrics>
        </PortfolioSnapshot>
      </div>

      <HoldingsSection>
        <CardHeader>
          <CardTitle>Top Holdings</CardTitle>
        </CardHeader>
        {topHoldings.length ? (
          <HoldingRows>
            {topHoldings.map((holding, index) => (
              <HoldingRow key={holding.propertyCode || index}>
                <HoldingIdentity>
                  <PropertyCodeBadge holding={holding} index={index} />
                  <div>
                    <HoldingName>
                      {holding.location || 'Investment'}
                    </HoldingName>
                    <HoldingMeta>{`${formatCount(holding.bricks)} Bricks`}</HoldingMeta>
                  </div>
                </HoldingIdentity>
                <HoldingValue>
                  {formatCurrency(holding.portfolioValue)}
                </HoldingValue>
                <HoldingRoi className={getValueTone(holding.roi)}>
                  {formatPercentage(holding.roi)}
                </HoldingRoi>
              </HoldingRow>
            ))}
          </HoldingRows>
        ) : (
          <EmptyCopy>No current holdings are available.</EmptyCopy>
        )}
        <CardFooter>
          <InlineAction type="button" onClick={onViewPortfolio}>
            View all holdings
            <FiArrowRight aria-hidden="true" />
          </InlineAction>
        </CardFooter>
      </HoldingsSection>
    </Card>
  );
};

OverviewPortfolioSummary.propTypes = {
  clientId: PropTypes.string.isRequired,
  detail: PropTypes.object.isRequired,
  onViewPortfolio: PropTypes.func.isRequired,
  summaryRef: PropTypes.object,
};

const RecentTransactions = ({
  transactions,
  onViewTransactions,
  properties,
  propertiesLoading,
}) => {
  const recentTransactions = getTransactionsWithActivity(transactions).slice(
    0,
    5,
  );
  return (
    <OverviewTransactionsLayout data-overview-section="transactions">
      <OverviewActivityCard>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        {recentTransactions.length ? (
          <DataTableScroll>
            <DataTable aria-label="Recent client transactions">
              <colgroup>
                <col className="activity-type-column" />
                <col className="activity-investment-column" />
                <col />
                <col className="activity-amount-column" />
              </colgroup>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Investment</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((transaction, index) => (
                  <tr key={transaction.id || `${transaction.date}-${index}`}>
                    <td>
                      <TransactionTypeBadge
                        title={transaction.activity.label}
                        className={`activity-type ${TRANSACTION_TYPES[transaction.activity.label]}`}
                      >
                        {transaction.activity.label}
                      </TransactionTypeBadge>
                    </td>
                    <td>
                      {transaction.activity.propertyCode ? (
                        <OrdersPropertyCode
                          order={transaction.activity}
                          properties={properties}
                          propertiesLoading={propertiesLoading}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <TransactionDescription
                        className="activity-description"
                        title={transaction.description}
                      >
                        {transaction.description || '—'}
                      </TransactionDescription>
                    </td>
                    <td>
                      <TransactionAmount
                        className={getValueTone(transaction.amount)}
                      >
                        {formatCurrency(transaction.amount)}
                      </TransactionAmount>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </DataTableScroll>
        ) : (
          <EmptyCopy>No recent wallet activity is available.</EmptyCopy>
        )}
        <CardFooter>
          <InlineAction type="button" onClick={onViewTransactions}>
            View all transactions
            <FiArrowRight aria-hidden="true" />
          </InlineAction>
        </CardFooter>
      </OverviewActivityCard>
    </OverviewTransactionsLayout>
  );
};

RecentTransactions.propTypes = {
  transactions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onViewTransactions: PropTypes.func.isRequired,
  properties: PropTypes.arrayOf(PropTypes.object),
  propertiesLoading: PropTypes.bool,
};

const ClientDetailsCard = ({ identity, onViewDetails, contentRef }) => {
  const rows = [
    { label: 'Name', value: identity.name },
    { label: 'Email', value: identity.email },
    { label: 'Mobile', value: identity.mobile },
    {
      label: 'Type',
      content: identity.accountTypeLabel ? (
        <AccountTypeBadge className={getAccountTypeTone(identity.accountType)}>
          {identity.accountTypeLabel}
        </AccountTypeBadge>
      ) : null,
    },
    {
      label: 'DOB',
      value: identity.dateOfBirth && formatDate(identity.dateOfBirth),
    },
    {
      label: 'Address',
      content: identity.address ? (
        <span className="client-address" title={identity.address}>
          {identity.address}
        </span>
      ) : null,
    },
  ].filter(({ value, content }) => value || content);

  return (
    <Card className="overview-client-details">
      <div className="overview-client-content" ref={contentRef}>
        <CardHeader>
          <CardTitle>Client Details</CardTitle>
        </CardHeader>
        <DetailRows rows={rows} />
      </div>
      <CardFooter>
        <InlineAction type="button" onClick={onViewDetails}>
          View all details
          <FiArrowRight aria-hidden="true" />
        </InlineAction>
      </CardFooter>
    </Card>
  );
};

ClientDetailsCard.propTypes = {
  identity: PropTypes.object.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  contentRef: PropTypes.object,
};

const PendingOrdersCard = ({
  detail,
  onViewOrders,
  properties,
  propertiesLoading,
}) => {
  if (detail.pendingOrderCount === null) return null;

  const previewOrders = (detail.orders || [])
    .slice()
    .sort(
      (left, right) =>
        getAdvisorDateTimestamp(right.date) -
        getAdvisorDateTimestamp(left.date),
    )
    .slice(0, 5);

  return (
    <OverviewActivityCard>
      <CardHeader>
        <CardTitle>Pending Orders</CardTitle>
      </CardHeader>
      {detail.orders === null ? (
        <EmptyCopy>Pending orders are currently unavailable.</EmptyCopy>
      ) : previewOrders.length ? (
        <DataTableScroll>
          <DataTable aria-label="Recent pending client orders">
            <colgroup>
              <col className="activity-type-column" />
              <col className="activity-investment-column" />
              <col className="activity-price-column" />
              <col />
              <col className="activity-amount-column activity-stacked-only" />
            </colgroup>
            <thead>
              <tr>
                <th>Type</th>
                <th>Investment</th>
                <th className="numeric">Price</th>
                <th className="numeric activity-quantity">Qty</th>
                <th className="numeric activity-stacked-only">Amount</th>
              </tr>
            </thead>
            <tbody>
              {previewOrders.map((order, index) => (
                <tr key={order.id || index}>
                  <td>
                    <StatusPill
                      className={
                        { buy: 'positive', sell: 'negative', pre: 'info' }[
                          order.side
                        ]
                      }
                    >
                      {formatStatus(order.side)}
                    </StatusPill>
                  </td>
                  <td>
                    <OrdersPropertyCode
                      order={order}
                      properties={properties}
                      propertiesLoading={propertiesLoading}
                    />
                  </td>
                  <td className="numeric">{formatCurrency(order.price)}</td>
                  <td className="numeric activity-quantity">
                    {formatCount(order.quantity)}
                  </td>
                  <td className="numeric activity-stacked-only">
                    <TransactionAmount
                      className={getValueTone(getSignedOrderAmount(order))}
                    >
                      {formatCurrency(getSignedOrderAmount(order))}
                    </TransactionAmount>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </DataTableScroll>
      ) : (
        <EmptyCopy>No pending orders.</EmptyCopy>
      )}
      <CardFooter>
        <InlineAction type="button" onClick={onViewOrders}>
          View all orders
          <FiArrowRight aria-hidden="true" />
        </InlineAction>
      </CardFooter>
    </OverviewActivityCard>
  );
};

PendingOrdersCard.propTypes = {
  detail: PropTypes.object.isRequired,
  onViewOrders: PropTypes.func.isRequired,
  properties: PropTypes.arrayOf(PropTypes.object),
  propertiesLoading: PropTypes.bool,
};

const DocumentsCard = ({
  detail,
  downloadingDocumentKey,
  onDownloadDocument,
  onViewDocuments,
}) => {
  const latestYear = getDocumentFinancialYears(detail.transactions)[0];
  const financialYearRange =
    formatFinancialYearRange(latestYear) || 'No eligible financial year';
  const documents = [
    {
      key: 'annual-pdf',
      year: latestYear,
      title: 'Latest Annual Statement',
      description: financialYearRange,
    },
    {
      key: 'tax-report',
      year: latestYear,
      title: 'Latest Tax Report',
      description: financialYearRange,
    },
  ];

  return (
    <Card className="overview-documents">
      <CardHeader>
        <CardTitle>Documents</CardTitle>
      </CardHeader>
      <DocumentRows>
        {documents.map((document) => {
          const DocumentIcon = getDocumentIcon(document.key);
          const downloadKey = document.year
            ? `${document.key}:${document.year}`
            : document.key;
          const busy = downloadingDocumentKey === downloadKey;
          const unavailableYear =
            document.key !== 'portfolio' && !document.year;
          return (
            <DocumentRow key={document.key}>
              <DocumentIdentity>
                <DocumentGlyph>
                  <DocumentIcon aria-hidden="true" />
                </DocumentGlyph>
                <div>
                  <DocumentName>{document.title}</DocumentName>
                  <DocumentMeta>{document.description}</DocumentMeta>
                </div>
              </DocumentIdentity>
              <DownloadButton
                type="button"
                aria-label={`Download ${document.title}`}
                disabled={Boolean(downloadingDocumentKey) || unavailableYear}
                title={
                  unavailableYear
                    ? 'No eligible financial year available'
                    : busy
                      ? 'Generating document...'
                      : `Download ${document.title}`
                }
                aria-busy={busy}
                onClick={() =>
                  onDownloadDocument(document.key, document.year, {
                    credentials: 'omit',
                  })
                }
              >
                {busy ? (
                  <FiLoader aria-hidden="true" />
                ) : (
                  <FiDownload aria-hidden="true" />
                )}
              </DownloadButton>
            </DocumentRow>
          );
        })}
      </DocumentRows>
      <CardFooter>
        <InlineAction type="button" onClick={onViewDocuments}>
          View all documents
          <FiArrowRight aria-hidden="true" />
        </InlineAction>
      </CardFooter>
    </Card>
  );
};

DocumentsCard.propTypes = {
  detail: PropTypes.object.isRequired,
  downloadingDocumentKey: PropTypes.string,
  onDownloadDocument: PropTypes.func.isRequired,
  onViewDocuments: PropTypes.func.isRequired,
};

const OverviewTab = () => {
  const {
    clientId,
    detail,
    downloadingDocumentKey,
    onDownloadDocument,
    onTabSelect,
    properties,
    propertiesLoading,
  } = useClientDetail();
  const layoutRef = useRef(null);
  const summaryRef = useRef(null);
  const clientContentRef = useRef(null);

  useEffect(() => {
    const blocks = [summaryRef.current, clientContentRef.current];
    const elements = blocks.reduce(
      (children, block) => [...children, ...Array.from(block.children)],
      [],
    );
    const updateHeight = () => {
      const height = Math.max(
        ...blocks.map((block) =>
          Array.from(block.children).reduce(
            (total, element) => total + element.getBoundingClientRect().height,
            0,
          ),
        ),
      );
      if (height > 0) {
        layoutRef.current.style.setProperty(
          '--overview-summary-height',
          `${height}px`,
        );
      }
    };
    updateHeight();
    const observer =
      typeof window.ResizeObserver === 'function'
        ? new window.ResizeObserver(updateHeight)
        : null;
    if (observer) elements.forEach((element) => observer.observe(element));
    window.addEventListener('resize', updateHeight);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [clientId, detail]);

  return (
    <OverviewLayout ref={layoutRef} data-test-reference="overview-layout">
      <OverviewPortfolioSummary
        clientId={clientId}
        detail={detail}
        onViewPortfolio={() => onTabSelect('portfolio')}
        summaryRef={summaryRef}
      />
      <OverviewColumn data-overview-section="identity">
        <ClientDetailsCard
          identity={detail.identity}
          onViewDetails={() => onTabSelect('account')}
          contentRef={clientContentRef}
        />
        <DocumentsCard
          detail={detail}
          downloadingDocumentKey={downloadingDocumentKey}
          onDownloadDocument={onDownloadDocument}
          onViewDocuments={() => onTabSelect('documents')}
        />
      </OverviewColumn>
      <RecentTransactions
        transactions={detail.transactions}
        onViewTransactions={() => onTabSelect('transactions')}
        properties={properties}
        propertiesLoading={propertiesLoading}
      />
      <OverviewColumn data-overview-section="activity">
        <PendingOrdersCard
          detail={detail}
          properties={properties}
          propertiesLoading={propertiesLoading}
          onViewOrders={() => onTabSelect('orders')}
        />
      </OverviewColumn>
    </OverviewLayout>
  );
};

export default OverviewTab;
